import { RightOutlined } from '@ant-design/icons';
import { ProductInventoryDoc, ProductInventoryUnit } from '@gooduncles/gu-app-schema';
import { Button } from 'antd';
import dayjs from 'dayjs';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { SupplierInboundItem } from 'src/reducers/supplierInboundReducer';

import { callSupplierInboundTransactions } from 'src/utils/firebase/firebase-callable';
import { getProductInboundTransactionId } from 'src/utils/firebase/firebase-short-cut';

import useSupplierInbound from 'src/hooks/reducer/useSupplierInbound';
import useProductInventoriesForSupplier from 'src/hooks/subscription/useProductInventoriesForSupplier';
import useProductSupplier from 'src/hooks/swr/useProductSupplier';
import useSupplierInboundStorage from 'src/hooks/useSessionStorage';
import { useTitle } from 'src/hooks/useTitle';

import ProductSupplierSelector from 'src/atomic-components/atoms/Seletor/ProductSupplierSelector';
import RequestSupplierInboundTable from 'src/atomic-components/organisms/Tables/RequestSupplierInboundTable/RequestSupplierInboundTable';
import SupplierInventoryTable, {
  SupplierInventoryTableRef,
} from 'src/atomic-components/organisms/Tables/SupplierInventoryTable';
import DatePickerWithArrows from 'src/components/Common/DatePickerWithArrows/DatePickerWithArrows';
import Loading from 'src/components/Loading/Loading';

import styles from './InventorySupplierInboundBoard.module.scss';

export type SupplierInventoryRow = {
  /** `${inventoryId}-${unit}` */
  key: string;
  inventoryId: string;
  inventoryName: string;
  unit: ProductInventoryUnit;
  ratio: number;
  quantity: number;
  cost: number;
  /** 환산재고 */
  stock: number;
};

/**
 * 재고 목록을 단위별로 분리합니다.
 * @param inventories 재고 목록
 * @returns 단위별 재고 목록
 */
const separateInventoriesByUnit = (inventories: ProductInventoryDoc[]) => {
  return inventories.flatMap((i) =>
    Object.entries(i.uom)
      .filter(([, uom]) => uom.cost !== undefined)
      .map(([unit, uom]) => ({
        key: `${i._id}-${unit}`,
        inventoryId: i._id,
        inventoryName: i.inventoryName,
        unit: unit as ProductInventoryUnit,
        ratio: uom.ratio,
        cost: uom.cost,
        stock: i.quantity / uom.ratio,
        quantity: i.quantity,
      }))
      // 가나다순 정렬 및 'ea' 단위가 먼저 오도록 정렬
      .sort((a, b) => {
        // 같은 이름일 경우 'ea' 단위가 먼저 오도록 정렬
        if (a.inventoryName === b.inventoryName) {
          if (a.unit === 'ea') return -1;
          if (b.unit === 'ea') return 1;
          return a.unit.localeCompare(b.unit);
        }
        // 이름 기준 가나다순 정렬
        return a.inventoryName.localeCompare(b.inventoryName);
      })
  );
};

const InventorySupplierInboundBoard: FC = () => {
  useTitle('거래처 입고 입력');
  const navigate = useNavigate();
  const { productSupplierId } = useParams();
  const { productSupplier, isLoading } = useProductSupplier(productSupplierId);
  const { productInventories, isLoading: isLoadingInventories } = useProductInventoriesForSupplier(productSupplierId);
  const inventoryUnitRows: SupplierInventoryRow[] = useMemo(
    () => separateInventoriesByUnit(productInventories ?? []),
    [productInventories]
  );
  const [selectedRows, setSelectedRows] = useState<SupplierInventoryRow[]>([]);
  const [date, setDate] = useState<dayjs.Dayjs | null>(dayjs());
  const supplierInventoryTableRef = useRef<SupplierInventoryTableRef>(null);

  // 거래처 입고 데이터 관리를 위한 리듀서 훅 사용
  const { items, addItems, updateItem, removeItem, clearItems, setItems } = useSupplierInbound();

  // 세션 스토리지 훅 사용
  const { removeFromStorage, cleanupStorage } = useSupplierInboundStorage(productSupplierId, date, items, setItems);

  // 세션 정리는 컴포넌트가 언마운트될 때도 호출
  useEffect(() => {
    return () => {
      cleanupStorage();
    };
  }, [cleanupStorage]);

  const supplierInboundRows = useMemo(
    () =>
      items.map((item) => {
        const inventory = inventoryUnitRows.find((row) => row.key === item.key);
        return {
          ...item,
          ratio: inventory?.ratio ?? 1,
          cost: inventory?.cost ?? 0,
          totalPrice: (inventory?.cost ?? 0) * item.requestValue,
        };
      }),
    [items, inventoryUnitRows]
  );

  const handleProductSupplierChange = (productSupplierId: string) => {
    clearItems();
    removeFromStorage();
    navigate(`/inventory/supplier-inbound/${productSupplierId}`);
  };

  // 선택된 항목을 입고 목록에 추가
  const handleAddSelectedItems = useCallback(() => {
    if (!selectedRows.length || !productSupplier) return;

    const newItems: SupplierInboundItem[] = selectedRows.map((row) => ({
      id: getProductInboundTransactionId(),
      key: row.key,
      inventoryId: row.inventoryId,
      inventoryName: row.inventoryName,
      unit: row.unit,
      requestValue: 1,
    }));

    if (newItems.length > 0) {
      addItems(newItems);
    }
    // 테이블의 선택 초기화
    supplierInventoryTableRef.current?.clearSelection();
  }, [selectedRows, productSupplier, addItems]);

  useEffect(() => {
    callSupplierInboundTransactions({
      body: null,
    }).then((res) => {
      console.log(`[preflight] callSupplierInboundTransactions: ${res.data.result}`, res);
    });
  }, []);

  return (
    <div className='height100 flexColumn'>
      <div className={styles.header}>
        <ProductSupplierSelector
          productSupplierId={productSupplierId}
          onProductSupplierChange={handleProductSupplierChange}
        />
        <DatePickerWithArrows date={date} setDate={setDate} />
      </div>

      <div className='flexRow flex1'>
        <div className='flex1 height100 flexColumn'>
          {isLoadingInventories ? (
            <Loading title='잠시만 기다려주세요.' />
          ) : (
            productSupplierId && (
              <SupplierInventoryTable
                ref={supplierInventoryTableRef}
                rowData={inventoryUnitRows}
                onSelectedRowsChange={setSelectedRows}
                clearItems={() => {
                  clearItems();
                  removeFromStorage();
                  supplierInventoryTableRef.current?.clearSelection();
                }}
              />
            )
          )}
        </div>
        {/* 버튼 구역 */}
        <div className='flexColumn flexCenter'>
          <Button
            icon={<RightOutlined style={{ fontSize: 32 }} />}
            disabled={!selectedRows.length}
            onClick={handleAddSelectedItems}
            style={{ width: 120, height: '100%', borderRadius: 4, marginTop: 40 }}
          />
        </div>

        <div className='flex1 height100 flexColumn'>
          {productSupplier && date ? (
            <RequestSupplierInboundTable
              date={date}
              rowData={supplierInboundRows}
              productSupplierId={productSupplierId ?? ''}
              updateItem={updateItem}
              removeItem={(id) => {
                removeItem(id);
                // 모든 항목이 삭제되면 로컬 스토리지에서도 삭제
                if (items.length <= 1) {
                  removeFromStorage();
                }
              }}
              clearItems={() => {
                clearItems();
                removeFromStorage();
              }}
            />
          ) : (
            <div className={styles.emptyContent}>공급업처와 입고일을 선택하면 해당 입고 상품 목록이 표시됩니다.</div>
          )}
        </div>

        {isLoading && <Loading title='잠시만 기다려주세요.' />}
      </div>
    </div>
  );
};

export default InventorySupplierInboundBoard;
